import createImageUrlBuilder from "@sanity/image-url";
import { FitMode } from "@sanity/image-url/lib/types/types";
import { useImageContext } from "../components/context";

const getSanityConfig = () => {
  if (!useImageContext()?.context?.sanityConfig) {
    console.warn("Sanity config is not set");
    return;
  }
  const { projectId, dataset } = useImageContext()?.context?.sanityConfig;
  return { projectId, dataset };
};

export const calculateImageFocusPoint = (image) => {
  const { hotspot } = image;
  if (hotspot?.x || hotspot?.y) {
    let x = hotspot?.x;
    let y = hotspot?.y;

    // add half of the width to x if width is less than 100% of image to set focus on center of the hotspot
    if (hotspot?.width < 1) {
      x = x + hotspot.width / 2;
    }

    // add half of the height to y if the height is less than 100% of image to set focus on center of the hotspot
    if (hotspot?.height < 1) {
      y = y + hotspot.height / 2;
    }
    return { x: x, y: y };
  } else {
    return;
  }
};

function getSanityImageURLWithCropAndFocalPoint(
  image: any,
  width: number = 800,
  height: number = 800,
  dataset: string,
  projectId: string,
  hotspot: { x?: number; y?: number },
  fit: FitMode,
  quality?: number
): string {
  const imageOrgWidth = image?.asset?.metadata?.dimensions?.width;
  const imageOrgHeight = image?.asset?.metadata?.dimensions?.height;

  const leftRect = Math.floor((image.crop?.left ?? 0) * imageOrgWidth!);
  const widthRect =
    Math.floor(imageOrgWidth! * (1 - (image.crop?.right ?? 0))) - leftRect;

  const topRect = Math.floor((image.crop?.top ?? 0) * imageOrgHeight!);
  const heightRect =
    Math.floor(imageOrgHeight! * (1 - (image.crop?.bottom ?? 0))) - topRect;

  return createImageUrlBuilder()
    .image(image)
    .dataset(dataset)
    .projectId(projectId)
    .fit(fit)
    .size(Math.round(width), Math.round(height))
    .quality(quality || 75)
    .rect(leftRect, topRect, widthRect, heightRect)
    .focalPoint(hotspot.x ?? 0.5, hotspot.y ?? 0.5)
    ?.url();
}

export function getSanityImageURL(
  image: any,
  width: number = 800,
  height: number = 800,
  fit: FitMode = "crop",
  quality?: number
) {
  if (getSanityConfig() === undefined) {
    return;
  }
  const { projectId, dataset } = getSanityConfig();
  if (!image) return "";

  const { hotspot } = image;

  if (hotspot?.x || hotspot?.y) {
    return getSanityImageURLWithCropAndFocalPoint(
      image,
      width,
      height,
      dataset,
      projectId,
      hotspot,
      fit,
      quality
    );
  } else {
    return createImageUrlBuilder()
      .image(image)
      .dataset(dataset)
      .projectId(projectId)
      .fit(fit)
      .size(Math.round(width), Math.round(height))
      .quality(quality || 75)
      ?.url();
  }
}
